import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from './PublicNav';
import ProductInquiryService from './ProductInquiryService';
import { CONTACT_PERKS, INTEGRATION_PILLS, PUBLIC_IMAGES } from './publicPageAssets';
import { PRODUCT_DISPLAY_NAME } from '../constants/branding';
import '../../assets/css/public-pages.css';

export default function ProductContact() {
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const onChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      await ProductInquiryService.sendProductInquiry({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        company: form.company.trim() || null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
      });
      setDone(true);
      setForm({ fullName: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pp-page">
      <PublicNav />

      <section className="pp-contact-hero">
        <div
          className="pp-contact-hero-bg"
          style={{ backgroundImage: `url(${PUBLIC_IMAGES.contact})` }}
        />
        <div className="pp-contact-hero-overlay" />
        <div className="pp-container pp-contact-hero-inner">
          <span className="pp-eyebrow pp-eyebrow--light">Get in touch</span>
          <h1 className="pp-title" style={{ color: '#fff', marginBottom: 12 }}>
            Contact sales
          </h1>
          <p className="pp-subtitle" style={{ color: 'rgba(255,255,255,0.82)', margin: '0 auto', maxWidth: 520 }}>
            Tell us about your agency or builder project — we&apos;ll follow up with pricing,
            onboarding, and a live {PRODUCT_DISPLAY_NAME} walkthrough.
          </p>
        </div>
      </section>

      <div className="pp-container pp-contact-layout">
        <div className="pp-contact-panel">
          <div className="pp-contact-panel-img">
            <img src={PUBLIC_IMAGES.property} alt="Real estate sales team" />
          </div>
          <div className="pp-contact-panel-body">
            <span className="pp-eyebrow">Why {PRODUCT_DISPLAY_NAME}</span>
            <h2 className="pp-title" style={{ fontSize: 22, marginBottom: 10 }}>
              We help agencies sell smarter
            </h2>
            <p className="pp-subtitle" style={{ fontSize: 14 }}>
              From Meta Lead Ads setup to team training — we partner with you beyond software delivery.
            </p>
            <ul className="pp-contact-perks">
              {CONTACT_PERKS.map((p) => (
                <li key={p.text}>
                  <i className={p.icon} aria-hidden />
                  <span>{p.text}</span>
                </li>
              ))}
            </ul>
            <div className="pp-hero-pills" style={{ marginTop: 24 }}>
              {INTEGRATION_PILLS.map((p) => (
                <span
                  key={p.label}
                  className="pp-hero-pill"
                  style={{
                    background: 'rgba(94,114,228,0.08)',
                    borderColor: 'rgba(94,114,228,0.15)',
                    color: '#344767',
                  }}
                >
                  <i className={p.icon} style={{ color: p.tone }} aria-hidden />
                  {p.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="pp-form-card">
          <h2 className="pp-title" style={{ fontSize: 22, marginBottom: 8 }}>
            Send us a message
          </h2>
          <p className="pp-subtitle" style={{ marginBottom: 24, fontSize: 14 }}>
            We typically respond within one business day.
          </p>

          {done ? (
            <div className="pp-form-success">
              <div>
                <i className="fas fa-check-circle" aria-hidden />
              </div>
              <p style={{ margin: 0, fontWeight: 700, color: '#344767' }}>
                Thanks — your message was sent.
              </p>
              <p style={{ margin: '10px 0 0', color: '#8392ab', fontSize: 14 }}>
                Our team will reply shortly.
              </p>
              <button
                type="button"
                onClick={() => setDone(false)}
                className="pp-btn-ghost"
                style={{
                  marginTop: 20,
                  color: '#344767',
                  borderColor: '#e9ecef',
                }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <label className="pp-form-field">
                <span className="pp-form-label">Full name *</span>
                <input
                  className="pp-input"
                  name="fullName"
                  value={form.fullName}
                  onChange={onChange}
                  required
                />
              </label>
              <label className="pp-form-field">
                <span className="pp-form-label">Work email *</span>
                <input
                  className="pp-input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                />
              </label>
              <label className="pp-form-field">
                <span className="pp-form-label">Phone</span>
                <input
                  className="pp-input"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                />
              </label>
              <label className="pp-form-field">
                <span className="pp-form-label">Company / agency</span>
                <input
                  className="pp-input"
                  name="company"
                  value={form.company}
                  onChange={onChange}
                />
              </label>
              <label className="pp-form-field">
                <span className="pp-form-label">Subject</span>
                <input
                  className="pp-input"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="e.g. Demo for 20-seat agency"
                />
              </label>
              <label className="pp-form-field">
                <span className="pp-form-label">How can we help? *</span>
                <textarea
                  className="pp-input"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  rows={5}
                  style={{ resize: 'vertical', minHeight: 120 }}
                />
              </label>
              {error && (
                <p style={{ color: '#f5365c', fontSize: 13, margin: '0 0 14px' }}>
                  <i className="fas fa-exclamation-circle" style={{ marginRight: 6 }} aria-hidden />
                  {error}
                </p>
              )}
              <button type="submit" disabled={sending} className="pp-form-submit">
                {sending ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}

          <p style={{ marginTop: 20, fontSize: 13, color: '#8392ab', textAlign: 'center' }}>
            Already a customer?{' '}
            <Link to="/login" style={{ color: '#5e72e4', fontWeight: 700 }}>
              Sign in to your CRM
            </Link>
          </p>
        </div>
      </div>

      <footer className="pp-footer">
        © {new Date().getFullYear()} {PRODUCT_DISPLAY_NAME} — All rights reserved.
      </footer>
    </div>
  );
}
